import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import { handleClick } from "lib/utils/navigator";
import { useFontSize } from "../FontSizeContext";
import { useHighlight, shouldHighlightText, isHighlightTarget, getMergedHighlightStyle } from "components/Resume/HighlightContext";
import { useMistakeAnalysis, isWordMistake, isSentenceMistake, isSingleWordMistake } from "components/Resume/MistakeAnalysisContext";

/**
 * Color used for highlighting mistake text in score-check
 */
const MISTAKE_TEXT_COLOR = '#d32f2f'; // Red color for mistakes

// export const ResumePDFSection = ({
//   themeColor,
//   heading,
//   style = {},
//   children,
// }: {
//   themeColor?: string;
//   heading?: string;
//   style?: Style;
//   children: React.ReactNode;
// }) => {
//   const handleClickCallback = useCallback(
//     () => handleClick(heading),
//     [heading]
//   );
//   return (
//     <div
//       onClick={handleClickCallback}
//       style={{
//         ...styles.flexCol,
//         gap: spacing["2"],
//         marginTop: spacing["5"],
//         cursor: "pointer",
//       }}
//     >
//         {heading && (
//           <View
//           minPresenceAhead={300}
//             style={{ ...styles.flexCol, gap: spacing["1"] }}
//             // minPresenceAhead={120}
//           >
//             <Text
//               style={{
//                 fontWeight: "bold",
//                 letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
//                 color: themeColor || DEFAULT_FONT_COLOR,
//               }}
//               debug={DEBUG_RESUME_PDF_FLAG}
//             >
//               {heading}
//             </Text>
//             {themeColor && (
//               <View
//                 style={{
//                   height: "1.25pt",
//                   width: "100%",
//                   backgroundColor: "black",
//                   marginRight: spacing["3.5"],
//                 }}
//                 debug={DEBUG_RESUME_PDF_FLAG}
//               />
//             )}
//           </View>
//         )}
//         {children}
      
//     </div>
//   );
// };
export const ResumePDFSection = ({
  themeColor,
  heading,
  isSectionSelected,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  isSectionSelected?: boolean;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(
    () => handleClick(heading),
    [heading]
  );
  return (
    <div onClick={handleClickCallback} style={{ cursor: "pointer" }}>
      <View
        wrap={false}
        style={{
          ...styles.flexCol,
          gap: spacing["1.5"], // Reduced gap for better space utilization
          marginTop: spacing["3"], // Reduced from spacing[5] for more content per page
          // cursor: "pointer",
        }}
      >
        {heading && (
          <View
            style={{ ...styles.flexCol, gap: spacing["0.5"] }} // Tighter gap for headings
            // minPresenceAhead={120}
          >
            <Text
              style={{
                ...dynamicStyles.heading, // Use dynamic heading styles with fontSize
                letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "black",
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        {children}
      </View>
    </div>
  );
};

export const ResumePDFSectionWithSubsections = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(
    () => handleClick(heading),
    [heading]
  );
  
  const childrenArray = React.Children.toArray(children);
  const firstChild = childrenArray[0];
  const restChildren = childrenArray.slice(1);
  
  return (
    <div onClick={handleClickCallback} style={{ cursor: "pointer" }}>
      {/* Wrap heading with first child to prevent orphan headings */}
      {heading && firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"],
            marginTop: spacing["3"], // Reduced from spacing[5] for better page utilization
          }}
          wrap={false}
        >
          {/* Heading */}
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["1"],
            }}
          >
            <Text
              style={{
                ...dynamicStyles.heading, // Use dynamic heading styles with fontSize
                letterSpacing: "0.3pt", 
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "black",
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
          {/* First child */}
          <View
            style={{
              marginTop: spacing["1.5"], // Reduced gap between heading and content
            }}
          >
            {firstChild}
          </View>
        </View>
      )}
      
      {/* Heading only (when no children) */}
      {heading && !firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["0.5"], // Tighter gap for headings
            marginTop: spacing["3"], // Reduced from spacing[5] for better space utilization
          }}
          // minPresenceAhead={300}
        >
          <Text
            style={{
              ...styles.heading, // Apply consistent heading styles
              letterSpacing: "0.3pt", 
              color: themeColor || DEFAULT_FONT_COLOR,
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
          {themeColor && (
            <View
              style={{
                height: "1.25pt",
                width: "100%",
                backgroundColor: "black",
                marginRight: spacing["3.5"],
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            />
          )}
        </View>
      )}
      
      {/* Rest of children */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"], // Reduced gap between subsections for better space utilization
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};

export const ResumePDFText = ({
  bold = false,
  italic = false,
  underline = false,
  themeColor,
  style = {},
  children,
}: {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  themeColor?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  return (
    <Text
      style={{
        ...dynamicStyles.text, // Use dynamic text styles with fontSize
        color: themeColor || DEFAULT_FONT_COLOR,
        fontWeight: bold ? "bold" : "normal",
        fontStyle: italic ? "italic" : "normal",
        textDecoration: underline ? "underline" : "none",
        ...style, // Allow style overrides
      }}
      debug={DEBUG_RESUME_PDF_FLAG}
    >
      {children}
    </Text>
  );
};

export const ResumePDFBulletList = ({
  items,
  showBulletPoints = true,
}: {
  items: string[];
  showBulletPoints?: boolean;
}) => {
  return (
    <>
      {items.map((item, idx) => (
        <View style={{ ...styles.flexRow }} key={idx}>
          {showBulletPoints && (
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
              }}
              bold={true}
            >
              {"•"}
            </ResumePDFText>
          )}
          {/* A breaking change was introduced causing text layout to be wider than node's width
              https://github.com/diegomura/react-pdf/issues/2182. flexGrow & flexBasis fixes it */}
          <ResumePDFText
            style={{ flexGrow: 1, flexBasis: 0 }}
          >
            {item}
          </ResumePDFText>
        </View>
      ))}
    </>
  );
};

export const ResumePDFLink = ({
  src,
  isPDF,
  children,
}: {
  src: string;
  isPDF: boolean;
  children: React.ReactNode;
}) => {
  if (isPDF) {
    return (
      <Link src={src} style={{ textDecoration: "none" }}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={src}
      style={{ textDecoration: "none" }}
      target="_blank"
      rel="noreferrer"
    >
      {children}
    </a>
  );
};

export const ProjectPDFLink = ({
  src,
  children,
}: {
  src: string;
  children: React.ReactNode;
}) => {
  return (
    <Link src={src} style={{ display: "flex", textDecoration: "none" }}>
      <a href={src} target="_blank">
        {children}
      </a>
    </Link>
  );
};

export const ResumeFeaturedSkill = ({
  skill,
  rating,
  themeColor,
  style = {},
}: {
  skill: string;
  rating: number;
  themeColor: string;
  style?: Style;
}) => {
  const numCircles = 5;

  return (
    <View style={{ ...styles.flexRow, alignItems: "center", ...style }}>
      <ResumePDFText style={{ marginRight: spacing[0.5] }}>
        {skill}
      </ResumePDFText>
      {[...Array(numCircles)].map((_, idx) => (
        <View
          key={idx}
          style={{
            height: "9pt",
            width: "9pt",
            marginLeft: "2.25pt",
            backgroundColor: rating >= idx ? themeColor : "#d9d9d9",
            borderRadius: "100%",
          }}
        />
      ))}
    </View>
  );
};

export const ResumePDFSkillsTwoColumn = ({ items }: { items: string[] }) => {
  const allSkills = items.join("");
  const skillNodes = extractSkillNodesFromHtml(allSkills);
  const midPoint = Math.ceil(skillNodes.length / 2);
  const firstColumn = skillNodes.slice(0, midPoint);
  const secondColumn = skillNodes.slice(midPoint);

  return (
    <View style={{ ...styles.flexRowBetween }}>
      <View style={{ ...styles.flexCol, width: "48.5%" }}>
        {firstColumn.map((node, idx) => (
          <React.Fragment key={idx}>{renderNode(node)}</React.Fragment>
        ))}
      </View>
      <View style={{ ...styles.flexCol, width: "48.5%" }}>
        {secondColumn.map((node, idx) => (
          <React.Fragment key={idx}>{renderNode(node)}</React.Fragment>
        ))}
      </View>
    </View>
  );
};

export const extractSkillNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes: ChildNode[] = [];

  function traverse(node: ChildNode) {
    if (node.nodeName === "P" || node.nodeName === "LI") {
      nodes.push(node);
    }

    node.childNodes.forEach(traverse);
  }

  doc.body.childNodes.forEach(traverse);

  return nodes;
};

// ============================================================================
// UNUSED FUNCTIONS - Kept for reference (replaced by unified renderNodeWithHighlight)
// ============================================================================
/**
 *  Render node with persistent mistake highlighting (red text) for score-check page
 * This is completely separate from hover highlighting to avoid interference
 * 
 * NOTE: This function is UNUSED - replaced by renderNodeWithHighlight which handles both hover and mistakes
 */
/* 
const renderNodeWithMistakes = (
  node: Node,
  currentSection: string,
  currentItemIndex: number | null
): React.ReactNode => {
  const mistakeAnalysis = useMistakeAnalysis();
  const highlight = useHighlight(); 
  const isTarget = isHighlightTarget(currentSection, currentItemIndex, highlight);
  
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const textContent = element.textContent || "";
    const nodeName = element.nodeName.toLowerCase();
    
    // Check if hover highlighting should be applied 
    const shouldHighlight = isTarget && 
      highlight.enabled && 
      highlight.mode === 'sentence' && 
      shouldHighlightText(textContent, highlight.texts, highlight.mode);
    
    switch (nodeName) {
      case "p":
        return (
          <View key={Math.random()}>
            <ResumePDFText 
              style={{ 
                flexGrow: 1,
                ...(shouldHighlight ? {
                  backgroundColor: highlight.style.backgroundColor,
                  color: highlight.style.color || highlight.style.textDecorationColor,
                  fontWeight: highlight.style.fontWeight as any,
                  textDecoration: highlight.style.textDecoration as any,
                  textDecorationStyle: highlight.style.textDecorationStyle as any,
                  textDecorationColor: highlight.style.textDecorationColor as any,
                } : {})
              }}
            >
              {parseAndTransformHTMLWithMistakes(element.innerHTML, currentSection, currentItemIndex, mistakeAnalysis, highlight, isTarget, nodeName)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
      case "ul":
        return (
          <View key={Math.random()} style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["0.5"],
                    }}
                    bold={true}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{
                      flex: 1,
                    }}
                  >
                    {parseAndTransformHTMLWithMistakes((child as Element).innerHTML, currentSection, currentItemIndex, mistakeAnalysis, highlight, isTarget)}
                  </ResumePDFText>
                </View>
              ) : (
                renderNodeWithMistakes(child, currentSection, currentItemIndex)
              )
            )}
          </View>
        );
        
      default:
        return null;
    }
  }
  
  return null;
};
*/

/**
 *  Parse HTML with mistake highlighting (red text for all mistakes)
 * Completely separate from parseAndTransformHTMLWithHighlight to avoid interference
 * 
 * NOTE: This function is UNUSED - replaced by parseAndTransformHTMLWithHighlight which handles both hover and mistakes
 */
/* 
const parseAndTransformHTMLWithMistakes = (
  htmlString: string,
  currentSection: string,
  currentItemIndex: number | null,
  mistakeAnalysis: ReturnType<typeof useMistakeAnalysis>,
  highlight: ReturnType<typeof useHighlight>,
  isTarget: boolean,
  parentNodeName?: string
): React.ReactNode[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  const transformNode = (node: Node, existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean } = {}) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const nodeName = element.nodeName.toLowerCase();

      let newStyles = { ...existingStyles };
      
      switch (nodeName) {
        case "strong":
        case "b":
          newStyles.bold = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "em":
        case "i":
          newStyles.italic = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "u":
          newStyles.underline = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "span":
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        default:
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      
      if (!text.trim()) return text;

      // Layer 1: Check for persistent mistakes (RED TEXT)
      const hasMistake = mistakeAnalysis.enabled && mistakeAnalysis.mistakeData && 
        isWordMistake(text, currentSection, currentItemIndex, mistakeAnalysis.mistakeData);

      // Layer 2: Check for hover highlighting (YELLOW BACKGROUND)
      let hoverStyle = {};
      if (isTarget && highlight.enabled && highlight.mode === 'word' && highlight.texts.length > 0) {
        // Check if this text matches hover highlight
        const shouldHover = shouldHighlightText(text, highlight.texts, 'word');
        if (shouldHover) {
          hoverStyle = {
            backgroundColor: highlight.style.backgroundColor,
            fontWeight: highlight.style.fontWeight as any,
            textDecoration: highlight.style.textDecoration as any,
            textDecorationStyle: highlight.style.textDecorationStyle as any,
            textDecorationColor: highlight.style.textDecorationColor as any,
          };
        }
      }

      // Combine both layers: red text (mistake) + yellow background (hover)
      const combinedStyle = {
        ...hoverStyle,
        ...(hasMistake ? { color: MISTAKE_TEXT_COLOR } : {}), // Red text overrides hover color
      };

      if (hasMistake || Object.keys(hoverStyle).length > 0) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            style={combinedStyle}
          >
            {text}
          </ResumePDFText>
        );
      }

      // No mistakes, no hover - render with existing styles only
      if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
          >
            {text}
          </ResumePDFText>
        );
      }
      
      return text;
    }
    
    return null;
  };

  doc.body.childNodes.forEach((node) => {
    const transformed = transformNode(node);
    if (transformed) {
      transformedChildren.push(transformed);
    }
  });

  return transformedChildren;
};
*/

export const ResumePDFDescription = ({
  items,
  enableHighlight = false,
  enableMistakeHighlight = false,
  section = 'experience',
  itemIndex = 0
}: { 
  items: string[];
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
  section?: string;
  itemIndex?: number | null;
}) => {
  // Check if items is defined and has at least one element
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const description = items[0];
  const newDescription = items.join("");
  // console.log("newDescription in pdf desc..", newDescription);
  // const descriptionNodes = extractNodesFromHtml(description);
  const descriptionNodes = extractNodesFromHtml(newDescription);

  //  highlighting or mistake highlighting is enabled
  if (enableHighlight || enableMistakeHighlight) {
    return (
      <View wrap={false}>
        {descriptionNodes.map((node, idx) => (
          <React.Fragment key={idx}>
            {renderNodeWithHighlight(node, section, itemIndex, enableMistakeHighlight)}
          </React.Fragment>
        ))}
      </View>
    );
  }

  // Plain rendering (no highlights, no mistake checks)
  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>
          {renderNode(node)}
        </React.Fragment>
      ))}
    </View>
  );
};

const parseAndTransformHTML = (htmlString: string): React.ReactNode[] => {
  // Create a temporary DOM element to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  // Recursive function to process and transform HTML nodes
  const transformNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.nodeName.toLowerCase()) {
        case "strong":
          return (
            <ResumePDFText key={Math.random()} bold={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "em":
          return (
            <ResumePDFText key={Math.random()} italic={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "u":
          return (
            <ResumePDFText key={Math.random()} underline={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {element.textContent}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          // Skip span elements
          return null;
        case "p":
          // return <Text key={Math.random()}>{element.textContent}</Text>;
          return (
            <View key={Math.random()}>
              <ResumePDFText>
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
              ;
            </View>
          );
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4, // adjust spacing as needed
                  paddingRight: 4, // adjust spacing as needed
                }}
                bold={true}
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{ flexGrow: 1, flexBasis: 0 }}
              >
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
            </View>
          );
        case "ol":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, index) =>
                transformNode(child)
              )}
            </View>
          );
        default:
          return null;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    return null;
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const transformedNode = transformNode(child);
    if (transformedNode) {
      transformedChildren.push(transformedNode);
    }
  });

  return transformedChildren;
};

/**
 * Enhanced HTML transformer with robust highlighting support
 * Handles: section/itemIndex filtering, layered highlighting (sentence + word),
 * style preservation (bold/italic/underline), and edge cases
 */
const parseAndTransformHTMLWithHighlight = (
  htmlString: string,
  currentSection: string,
  currentItemIndex: number | null,
  parentNodeName?: string,
  enableMistakeHighlight: boolean = false,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];
  const highlight = useHighlight();

  // Check if this item should be highlighted at all
  const isTarget = isHighlightTarget(currentSection, currentItemIndex, highlight);

  const transformNode = (node: Node, existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean } = {}) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const nodeName = element.nodeName.toLowerCase();

      // Track existing formatting
      let newStyles = { ...existingStyles };
      
      switch (nodeName) {
        case "strong":
        case "b":
          newStyles.bold = true;
          // Recursively transform children with bold style
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "em":
        case "i":
          newStyles.italic = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "u":
          newStyles.underline = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {Array.from(element.childNodes).map((child, idx) => (
                  <React.Fragment key={`${Math.random()}-${idx}`}>
                    {transformNode(child, { ...newStyles, underline: true })}
                  </React.Fragment>
                ))}
              </ResumePDFText>
            </ResumePDFLink>
          );
          
        case "span":
          // Process span children (don't skip)
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "p":
          return (
            <View key={Math.random()}>
              <ResumePDFText>
                {Array.from(element.childNodes).map((child, idx) => (
                  <React.Fragment key={`${Math.random()}-${idx}`}>
                    {transformNode(child, newStyles)}
                  </React.Fragment>
                ))}
              </ResumePDFText>
            </View>
          );
          
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4,
                  paddingRight: 4,
                }}
                bold={true}
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText style={{ flexGrow: 1, flexBasis: 0 }}>
                {Array.from(element.childNodes).map((child, idx) => (
                  <React.Fragment key={`${Math.random()}-${idx}`}>
                    {transformNode(child, newStyles)}
                  </React.Fragment>
                ))}
              </ResumePDFText>
            </View>
          );
          
        case "ol":
        case "ul":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </View>
          );
          
        default:
          // For unknown elements, process children
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      
      if (!text.trim()) return text; // Return whitespace as-is
      
      // Checking if this is a sentence-level mistake (entire sentence should be red)
      const isSentenceError = enableMistakeHighlight && 
        mistakeAnalysis?.enabled && 
        mistakeAnalysis?.mistakeData &&
        isSentenceMistake(text, currentSection, currentItemIndex, mistakeAnalysis.mistakeData);
      
      // If it's a sentence-level mistake, we need to color the entire text red
      // BUT we also need to check for word-level hover highlighting within this red sentence
      if (isSentenceError) {
        // Check if we also need WORD-level hover highlighting within this red sentence
        if (isTarget && highlight.enabled && highlight.mode === 'word' && highlight.texts.length > 0) {
          // Red sentence + word hover: Use word splitting with forceRedText flag
          return renderTextWithWordHighlights(
            text, 
            highlight, 
            existingStyles,
            currentSection, 
            currentItemIndex ?? null, 
            mistakeAnalysis,
            isTarget,
            enableMistakeHighlight,
            true // Force all text parts to be red (sentence mistake)
          );
        }
        
        // Check for SENTENCE-level hover highlighting
        if (isTarget && highlight.enabled && highlight.mode === 'sentence' && highlight.texts.length > 0) {
          const shouldHighlight = shouldHighlightText(text, highlight.texts, 'sentence');
          const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, shouldHighlight);
          mergedStyle.color = MISTAKE_TEXT_COLOR; // Red text overrides
          return (
            <ResumePDFText key={Math.random()} style={mergedStyle}>
              {text}
            </ResumePDFText>
          );
        }
        
        // No hover at all - just red text for entire sentence
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            style={{ color: MISTAKE_TEXT_COLOR }}
          >
            {text}
          </ResumePDFText>
        );
      }
      
      // Not a sentence mistake - handle word-level rendering
      // This includes: word hover highlights, word mistake highlights, or both
      
      // Check if we need word-level processing (hover words OR mistake analysis)
      const needsWordProcessing = 
        (isTarget && highlight.enabled && highlight.mode === 'word' && highlight.texts.length > 0) ||
        (enableMistakeHighlight && mistakeAnalysis?.enabled);
      
      if (needsWordProcessing) {
        // Use unified word-splitting logic that handles BOTH hover AND mistakes
        return renderTextWithWordHighlights(
          text, 
          highlight, 
          existingStyles,
          currentSection, 
          currentItemIndex ?? null, 
          mistakeAnalysis,
          isTarget,
          enableMistakeHighlight
        );
      }
      
      // No word processing needed - check for sentence hover highlighting
      if (isTarget && highlight.enabled && highlight.mode === 'sentence' && highlight.texts.length > 0) {
        const shouldHighlight = shouldHighlightText(text, highlight.texts, 'sentence');
        const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, shouldHighlight);
        return (
          <ResumePDFText key={Math.random()} style={mergedStyle}>
            {text}
          </ResumePDFText>
        );
      }

      // No highlighting at all - render with existing styles only
      if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
          >
            {text}
          </ResumePDFText>
        );
      }
      
      return text;
    }
    
    return null;
  };

  doc.body.childNodes.forEach((node) => {
    const transformed = transformNode(node);
    if (transformed) {
      transformedChildren.push(transformed);
    }
  });

  return transformedChildren;
};

/**
 * Handles: multiple matches, partial matches, case-insensitive, Unicode
 * +  supports mistake highlighting (red text)
 */
/**
 * Unified word-level rendering that handles BOTH hover highlighting AND mistake highlighting
 * 
 * Preserves all edge cases: multiple matches, partial matches, case-insensitive, Unicode, ALL occurrences
 */
const renderTextWithWordHighlights = (
  text: string,
  highlight: ReturnType<typeof useHighlight>,
  existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean },
  currentSection?: string,
  currentItemIndex?: number | null,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>,
  isHoverTarget: boolean = false,
  enableMistakeHighlight: boolean = false,
  forceRedText: boolean = false // Force all text to be red (for sentence-level mistakes)
): React.ReactNode => {
  try {
    // Determine what we need to check
    const shouldCheckHover = isHoverTarget && highlight.enabled && highlight.mode === 'word' && highlight.texts.length > 0;
    const shouldCheckMistakes = enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && currentSection;
    
    // Build regex pattern for hover highlight words 
    const words = shouldCheckHover
      ? highlight.texts
          .map(w => (w || "").trim())
          .filter(Boolean)
          .map(w => {
            // Escape special regex characters
            const escaped = w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            return escaped;
          })
          .join("|")
      : "";

    // If no hover words and no mistakes and not forced red, render simple
    if (!words && !shouldCheckMistakes && !forceRedText) {
      if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
          >
            {text}
          </ResumePDFText>
        );
      }
      return text;
    }

    // STEP 1: Split by hover regex 
    const parts: Array<{ text: string; isHoverMatch: boolean }> = [];
    
    if (words) {
      // Create regex with word boundaries and case-insensitive flag (EXACT SAME as original)
      const re = new RegExp(`\\b(${words})\\b`, "giu"); // 'u' for Unicode support
      let lastIndex = 0;
      let m: RegExpExecArray | null;
      
      while ((m = re.exec(text)) !== null) {
        const matchStart = m.index;
        const matchStr = m[0];
        
        // Add non-matched text before this match
        if (matchStart > lastIndex) {
          parts.push({ text: text.substring(lastIndex, matchStart), isHoverMatch: false });
        }
        
        // Add matched text
        parts.push({ text: matchStr, isHoverMatch: true });
        lastIndex = matchStart + matchStr.length;
      }
      
      // Add remaining text after last match
      if (lastIndex < text.length) {
        parts.push({ text: text.substring(lastIndex), isHoverMatch: false });
      }
      
      // If no matches found, treat entire text as one part
      if (parts.length === 0) {
        parts.push({ text, isHoverMatch: false });
      }
    } else {
      // No hover words - treat entire text as one part
      parts.push({ text, isHoverMatch: false });
    }

    // STEP 2: For each part, check if it's a mistake word
    // For NON-hover parts, we need to split by word boundaries to check each word
    // For hover parts, just check if the matched word is also a mistake
    const finalParts: Array<{ text: string; isHoverMatch: boolean; isMistake: boolean }> = [];
    
    for (const part of parts) {
      if (shouldCheckMistakes && !part.isHoverMatch && part.text.trim()) {
        // Non-hover part with mistake checking: split by word boundaries
        // Use same splitting approach as original to preserve punctuation
        const wordBoundaryParts = part.text.split(/(\s+)/);
        
        for (const wordPart of wordBoundaryParts) {
          if (!wordPart) continue;
          
          // Check if this word (excluding whitespace) is a mistake
          const trimmed = wordPart.trim();
          const isMistake = trimmed.length > 0 && isSingleWordMistake(
            trimmed,
            currentSection!,
            currentItemIndex ?? null,
            mistakeAnalysis!.mistakeData
          );
          
          finalParts.push({
            text: wordPart,
            isHoverMatch: false,
            isMistake
          });
        }
      } else if (shouldCheckMistakes && part.isHoverMatch) {
        // Hover match: check if this matched word is also a mistake
        const isMistake = isSingleWordMistake(
          part.text.trim(),
          currentSection!,
          currentItemIndex ?? null,
          mistakeAnalysis!.mistakeData
        );
        
        finalParts.push({
          text: part.text,
          isHoverMatch: true,
          isMistake
        });
      } else {
        // No mistake checking, or empty text - just use hover status
        finalParts.push({
          text: part.text,
          isHoverMatch: part.isHoverMatch,
          isMistake: false
        });
      }
    }

    // STEP 3: Render all parts with appropriate styling
    return (
      <>
        {finalParts.map((part, i) => {
          // Determine final color
          const isRed = forceRedText || part.isMistake; // Red if forced (sentence mistake) OR word mistake
          
          if (part.isHoverMatch) {
            // Hover match: apply yellow background
            const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, true);
            if (isRed) {
              mergedStyle.color = MISTAKE_TEXT_COLOR; // Red text overrides
            }
            return (
              <ResumePDFText
                key={`${Math.random()}-${i}`}
                style={mergedStyle}
              >
                {part.text}
              </ResumePDFText>
            );
          } else {
            // Not a hover match
            if (isRed || existingStyles.bold || existingStyles.italic || existingStyles.underline) {
              return (
                <ResumePDFText
                  key={`${Math.random()}-${i}`}
                  bold={existingStyles.bold}
                  italic={existingStyles.italic}
                  underline={existingStyles.underline}
                  style={isRed ? { color: MISTAKE_TEXT_COLOR } : {}}
                >
                  {part.text}
                </ResumePDFText>
              );
            }
            return <React.Fragment key={`${Math.random()}-${i}`}>{part.text}</React.Fragment>;
          }
        })}
      </>
    );
  } catch (e) {
    console.warn("Error in renderTextWithWordHighlights:", e);
    // Fallback: render plain text with existing styles
    const fallbackRed = forceRedText ? { color: MISTAKE_TEXT_COLOR } : {};
    if (existingStyles.bold || existingStyles.italic || existingStyles.underline || forceRedText) {
      return (
        <ResumePDFText
          key={Math.random()}
          bold={existingStyles.bold}
          italic={existingStyles.italic}
          underline={existingStyles.underline}
          style={fallbackRed}
        >
          {text}
        </ResumePDFText>
      );
    }
    return text;
  }
};

/**
 * Enhanced node renderer with highlight support
 * Handles section/itemIndex filtering and preserves existing formatting
 */
const renderNodeWithHighlight = (
  node: Node,
  currentSection: string,
  currentItemIndex: number | null,
  enableMistakeHighlight: boolean = false
): React.ReactNode => {
  const highlight = useHighlight();
  const mistakeAnalysis = useMistakeAnalysis();
  
  // Check if this item should be highlighted
  const isTarget = isHighlightTarget(currentSection, currentItemIndex, highlight);
  
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const textContent = element.textContent || "";
    const nodeName = element.nodeName.toLowerCase();
    
    // Check if the entire content should be highlighted (sentence-level)
    const shouldHighlight = isTarget && 
      highlight.enabled && 
      highlight.mode === 'sentence' && 
      shouldHighlightText(textContent, highlight.texts, highlight.mode);
    
    switch (nodeName) {
      case "p":
        return (
          <View key={Math.random()}>
            <ResumePDFText 
              style={{ 
                flexGrow: 1,
                ...(shouldHighlight ? {
                  backgroundColor: highlight.style.backgroundColor,
                  color: highlight.style.color || highlight.style.textDecorationColor,
                  fontWeight: highlight.style.fontWeight as any,
                  textDecoration: highlight.style.textDecoration as any,
                  textDecorationStyle: highlight.style.textDecorationStyle as any,
                  textDecorationColor: highlight.style.textDecorationColor as any,
                } : {})
              }}
            >
              {parseAndTransformHTMLWithHighlight(element.innerHTML, currentSection, currentItemIndex, nodeName, enableMistakeHighlight, mistakeAnalysis)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
      case "ul":
        return (
          <View key={Math.random()} style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                    }}
                    bold={true}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText style={{ flexGrow: 1, flexBasis: 0 }}>
                    {parseAndTransformHTMLWithHighlight((child as Element).innerHTML, currentSection, currentItemIndex, 'li', enableMistakeHighlight, mistakeAnalysis)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
        
      case "li":
        const liShouldHighlight = isTarget && 
          highlight.enabled && 
          highlight.mode === 'sentence' && 
          shouldHighlightText(textContent, highlight.texts, highlight.mode);
          
        return (
          <View key={Math.random()} style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
              }}
              bold={true}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                flexGrow: 1,
                flexBasis: 0,
                ...(liShouldHighlight ? {
                  backgroundColor: highlight.style.backgroundColor,
                  color: highlight.style.color || highlight.style.textDecorationColor,
                  fontWeight: highlight.style.fontWeight as any,
                  textDecoration: highlight.style.textDecoration as any,
                  textDecorationStyle: highlight.style.textDecorationStyle as any,
                  textDecorationColor: highlight.style.textDecorationColor as any,
                } : {} )
              }}
            >
              {parseAndTransformHTMLWithHighlight(element.innerHTML, currentSection, currentItemIndex, nodeName)}
            </ResumePDFText>
          </View>
        );
        
      default:
        return null;
    }
  }
  return null;
};

const renderNode = (node: Node): React.ReactNode => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    switch (element.nodeName.toLowerCase()) {
      case "p":
        // return <Text key={Math.random()}>{element.textContent}</Text>;
        return (
          <View>
            <ResumePDFText style={{ flexGrow: 1 }}>
              {parseAndTransformHTML(element.innerHTML)}
              <br />
            </ResumePDFText>
          </View>
        );
      case "ol":
        return (
          <View key={Math.random()} style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                    }}
                    bold={true}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ flexGrow: 1, flexBasis: 0 }}
                  >
                    {parseAndTransformHTML((child as Element).innerHTML)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
      case "li":
        return (
          <View key={Math.random()} style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"], // adjust spacing as needed
                paddingRight: spacing["2"], // adjust spacing as needed
              }}
              bold={true}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                flexGrow: 1,
                flexBasis: 0,
              }}
            >
              {parseAndTransformHTML(element.innerHTML)}
            </ResumePDFText>
          </View>
        );
      default:
        return null;
    }
  }
  return null;
};

export const extractNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
};

/**
 * Component to render highlightable plain text (for profile summary)
 * Supports word-level highlighting for spelling mistakes, buzzwords, etc.
 * AND mistake highlighting (red text for mistakes)
 */
export const ResumePDFHighlightableText = ({
  text,
  section,
  itemIndex = null,
  style = {},
  enableMistakeHighlight = false,
}: {
  text: string;
  section: string;
  itemIndex?: number | null;
  style?: Style;
  enableMistakeHighlight?: boolean;
}) => {
  const highlight = useHighlight();
  const mistakeAnalysis = useMistakeAnalysis();
  
  // Check if this item should be highlighted
  const isTarget = isHighlightTarget(section, itemIndex, highlight);
  const hoverEnabled = isTarget && highlight.enabled && highlight.mode === 'word';
  
  // If neither hover highlighting nor mistake highlighting is enabled, render plain text
  if (!hoverEnabled && !enableMistakeHighlight) {
    return <ResumePDFText style={style}>{text}</ResumePDFText>;
  }
  
  // Split text into words and apply highlighting
  const words = text.split(/(\s+)/); // Keep whitespace in the result
  
  return (
    <ResumePDFText style={style}>
      {words.map((word, index) => {
        const normalizedWord = word.trim().toLowerCase();
        
        // Check if word should be hover-highlighted
        const shouldHoverHighlight = hoverEnabled && highlight.texts.some(
          mistakeText => normalizedWord.includes(mistakeText.toLowerCase())
        );
        
        // Check if word is a mistake (for red text)
        const isMistake = enableMistakeHighlight && 
          mistakeAnalysis && 
          word.trim().length > 0 && 
          isSingleWordMistake(
            word.trim(),
            section,
            itemIndex,
            mistakeAnalysis.mistakeData
          );
        
        // Apply appropriate styling
        if (shouldHoverHighlight && isMistake) {
          // Both hover and mistake: yellow background + red text
          return (
            <Text
              key={index}
              style={{
                backgroundColor: highlight.style.backgroundColor,
                color: MISTAKE_TEXT_COLOR,
                fontWeight: highlight.style.fontWeight as any,
                textDecoration: highlight.style.textDecoration as any,
                textDecorationStyle: highlight.style.textDecorationStyle as any,
                textDecorationColor: highlight.style.textDecorationColor as any,
              }}
            >
              {word}
            </Text>
          );
        } else if (shouldHoverHighlight) {
          // Only hover: yellow background
          return (
            <Text
              key={index}
              style={{
                backgroundColor: highlight.style.backgroundColor,
                color: highlight.style.color || highlight.style.textDecorationColor,
                fontWeight: highlight.style.fontWeight as any,
                textDecoration: highlight.style.textDecoration as any,
                textDecorationStyle: highlight.style.textDecorationStyle as any,
                textDecorationColor: highlight.style.textDecorationColor as any,
              }}
            >
              {word}
            </Text>
          );
        } else if (isMistake) {
          // Only mistake: red text
          return (
            <Text
              key={index}
              style={{
                color: MISTAKE_TEXT_COLOR,
              }}
            >
              {word}
            </Text>
          );
        }
        
        return <Text key={index}>{word}</Text>;
      })}
    </ResumePDFText>
  );
};

